import * as THREE from 'three';
import { getSprites } from './textures';
import { EARTH, HIT_RADIUS, makeRng } from './geodesic';

/** A colour whose channels may go above 1, so it blooms. Values are linear. */
export const hdr = (r: number, g: number, b: number) => new THREE.Color(r, g, b);

function sprite(map: THREE.Texture, color: THREE.Color, scale: number, opacity = 1): THREE.Sprite {
  const m = new THREE.SpriteMaterial({ map, color, opacity, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false });
  const s = new THREE.Sprite(m);
  s.scale.set(scale, scale, 1);
  return s;
}

const SUN_VERT = /* glsl */ `
varying vec3 vN; varying vec3 vV; varying vec3 vP;
void main() {
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  vN = normalize(normalMatrix * normal);
  vV = normalize(-mv.xyz);
  vP = position;
  gl_Position = projectionMatrix * mv;
}`;

const SUN_FRAG = /* glsl */ `
uniform float uTime; uniform vec3 uColor;
varying vec3 vN; varying vec3 vV; varying vec3 vP;
void main() {
  float mu = clamp(dot(normalize(vN), normalize(vV)), 0.0, 1.0);
  float limb = 0.28 + 0.72 * pow(mu, 0.85);
  float g1 = sin(vP.x * 9.0 + uTime * 0.31) * sin(vP.y * 8.0 - uTime * 0.23) * sin(vP.z * 10.0 + uTime * 0.17);
  float g2 = sin(vP.x * 23.0 - uTime * 0.41 + vP.z * 7.0) * sin(vP.y * 19.0 + uTime * 0.37 - vP.x * 5.0);
  float gran = 0.9 + 0.07 * g1 + 0.03 * g2;
  gl_FragColor = vec4(uColor * limb * gran, 1.0);
}`;

export interface Sun {
  group: THREE.Group;
  update(time: number): void;
}

/** The Sun at the origin: an emissive sphere with limb darkening, and three additive glows of growing size and fading strength. */
export function createSun(): Sun {
  const { soft, hard } = getSprites();
  const group = new THREE.Group();
  const mat = new THREE.ShaderMaterial({
    uniforms: { uTime: { value: 0 }, uColor: { value: hdr(3.0, 1.6, 0.6) } },
    vertexShader: SUN_VERT,
    fragmentShader: SUN_FRAG
  });
  group.add(new THREE.Mesh(new THREE.SphereGeometry(1, 64, 32), mat));
  const warm = hdr(2.4, 1.15, 0.38);
  const halos: [number, number, THREE.Color][] = [[3.2, 0.45, hdr(2.6, 1.3, 0.45)], [6.0, 0.15, warm], [11.5, 0.05, hdr(1.8, 0.8, 0.3)]];
  for (const [scale, opacity, color] of halos) group.add(sprite(soft, color, scale, opacity));
  group.add(sprite(hard, hdr(1.6, 1.0, 0.5), 2.3, 0.2));
  return { group, update(time: number) { mat.uniforms.uTime.value = time; } };
}

export interface EarthMarker {
  group: THREE.Group;
  /** 0..1 brightening, used when the starlight arrives */
  setFlash(v: number): void;
}

/** Earth with its telescope: a small blue sprite, a soft halo and a thin ring at the size of the target. */
export function createEarth(): EarthMarker {
  const { soft, hard } = getSprites();
  const group = new THREE.Group();
  group.position.set(EARTH.x, EARTH.y, 0);
  const ice = hdr(0.5, 1.15, 2.2);
  const core = sprite(hard, ice, 0.55);
  const halo = sprite(soft, hdr(0.26, 0.6, 1.2), 2.2, 0.55);
  group.add(halo, core);
  const ring: number[] = [];
  const N = 64;
  for (let i = 0; i < N; i++) { const a = (i / N) * Math.PI * 2; ring.push(HIT_RADIUS * Math.cos(a), HIT_RADIUS * Math.sin(a), 0); }
  const rg = new THREE.BufferGeometry();
  rg.setAttribute('position', new THREE.Float32BufferAttribute(ring, 3));
  const ringMat = new THREE.LineBasicMaterial({ color: hdr(0.6, 1.2, 1.7), transparent: true, opacity: 0.5, blending: THREE.AdditiveBlending, depthWrite: false });
  group.add(new THREE.LineLoop(rg, ringMat));
  return {
    group,
    setFlash(v: number) {
      (halo.material as THREE.SpriteMaterial).opacity = 0.55 + 0.45 * v;
      halo.scale.setScalar(2.2 + 2.2 * v);
      (core.material as THREE.SpriteMaterial).color.setRGB(0.5 + 2.0 * v, 1.15 + 1.6 * v, 2.2 + 1.2 * v);
      ringMat.opacity = 0.5 + 0.5 * v;
    }
  };
}

/** About 1500 stars on a sphere of radius 180, slightly varied in colour and brightness. Two sizes, so a few shine. */
export function createStars(): THREE.Group {
  const { hard } = getSprites();
  const rng = makeRng(2024);
  const group = new THREE.Group();
  const make = (count: number, size: number, gain: number) => {
    const pos = new Float32Array(count * 3), col = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const z = 2 * rng() - 1, a = rng() * Math.PI * 2, s = Math.sqrt(1 - z * z);
      pos[3 * i] = 180 * s * Math.cos(a); pos[3 * i + 1] = 180 * s * Math.sin(a); pos[3 * i + 2] = 180 * z;
      const warm = rng(), lum = (0.3 + 0.7 * rng() * rng()) * gain;
      col[3 * i] = lum * (0.78 + 0.22 * warm); col[3 * i + 1] = lum * (0.84 + 0.1 * warm); col[3 * i + 2] = lum * (1.0 - 0.28 * warm);
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    g.setAttribute('color', new THREE.BufferAttribute(col, 3));
    const m = new THREE.PointsMaterial({ map: hard, size, vertexColors: true, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, sizeAttenuation: true });
    return new THREE.Points(g, m);
  };
  group.add(make(1250, 0.9, 0.9), make(250, 1.8, 1.5));
  return group;
}
